<template>
  <transition name="legal-modal-fade">
    <div
      v-if="visible"
      class="legal-modal"
      role="presentation"
      @click.self="close"
    >
      <section
        ref="dialog"
        class="legal-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="legal-modal-title"
        tabindex="-1"
      >
        <header class="legal-modal__header">
          <div class="legal-modal__heading-row">
            <div>
              <h2 id="legal-modal-title">法律条款</h2>
              <p>最后更新：2026 年 7 月 1 日 · 版本 v3.2</p>
            </div>
            <button
              class="legal-modal__close"
              type="button"
              aria-label="关闭法律条款"
              @click="close"
            >
              <i class="el-icon-close" aria-hidden="true" />
            </button>
          </div>

          <div class="legal-modal__tabs" role="tablist" aria-label="法律条款类型">
            <button
              id="legal-tab-terms"
              :class="{ active: activeTab === 'terms' }"
              type="button"
              role="tab"
              :aria-selected="String(activeTab === 'terms')"
              aria-controls="legal-modal-content"
              @click="selectTab('terms')"
            >
              服务条款
            </button>
            <button
              id="legal-tab-privacy"
              :class="{ active: activeTab === 'privacy' }"
              type="button"
              role="tab"
              :aria-selected="String(activeTab === 'privacy')"
              aria-controls="legal-modal-content"
              @click="selectTab('privacy')"
            >
              隐私政策
            </button>
          </div>
        </header>

        <div
          id="legal-modal-content"
          ref="content"
          class="legal-modal__content"
          role="tabpanel"
          :aria-labelledby="activeTab === 'terms' ? 'legal-tab-terms' : 'legal-tab-privacy'"
          tabindex="0"
        >
          <div class="legal-modal__intro">{{ currentContent.intro }}</div>

          <section
            v-for="section in currentContent.sections"
            :key="`${activeTab}-${section.no}`"
            class="legal-modal__section"
          >
            <h3>
              <span>{{ section.no }}</span>
              {{ section.title }}
            </h3>
            <p v-for="paragraph in section.paragraphs" :key="paragraph">
              {{ paragraph }}
            </p>
          </section>

          <p class="legal-modal__contact">
            如对本条款有任何疑问，请通过帮助中心提交工单，或发送邮件至
            support@uplustrade.com，我们将在 2 个工作日内答复。
          </p>
        </div>

        <footer class="legal-modal__footer">
          <button type="button" @click="close">我已阅读</button>
        </footer>
      </section>
    </div>
  </transition>
</template>

<script>
const LEGAL_CONTENT = {
  terms: {
    intro:
      '本条款是您与 UPlus 之间就使用量化交易服务达成的协议。UPlus 仅提供策略执行工具，不吸收存款、不代客理财、不承诺收益。请在使用前完整阅读，尤其是第 04 条「风险揭示」与第 06 条「责任限制」。',
    sections: [
      {
        no: '01',
        title: '服务性质',
        paragraphs: [
          'UPlus 是一款自动化交易执行工具。您通过绑定交易所 API 授权本平台按您选定的策略参数下达委托，资产始终存放于您本人的交易所账户，UPlus 不托管、不转移您的资金。',
          '平台提供的历史收益、回测数据与收益榜单仅供参考，不构成投资建议，亦不代表未来表现。'
        ]
      },
      {
        no: '02',
        title: '账户与 API 授权',
        paragraphs: [
          '您应确保注册信息真实有效，并妥善保管账户密码与邮箱验证码。因您自身原因导致账户被他人使用所产生的损失，由您自行承担。',
          '绑定 API 时请仅授予“读取”与“现货/合约交易”权限，切勿开启提币权限。UPlus 系统在技术上不会调用任何提币接口。',
          '您可随时在个人中心解绑 API，解绑后关联的运行中策略将自动停止。'
        ]
      },
      {
        no: '03',
        title: '费用与结算',
        paragraphs: [
          '平台采用利润分成模式：仅在策略产生正收益时，按已实现盈利的约定比例收取服务费，不盈利不收费。具体比例以创建策略时页面展示及您所属的费率方案为准。',
          '服务费于每笔收益结算时自动扣除，优先扣除点卡余额，点卡不足时从资金账户扣除。点卡一经购买不可提现、退换或转让。',
          '交易所自身收取的手续费、资金费率与滑点不属于平台费用，由交易所直接结算。'
        ]
      },
      {
        no: '04',
        title: '风险揭示',
        paragraphs: [
          '数字资产价格波动剧烈，量化策略无法消除市场风险。使用合约类策略可能因剧烈行情导致保证金不足乃至强制平仓，您可能损失全部投入本金。',
          '策略运行依赖交易所 API 的可用性。交易所维护、限流、宕机或网络异常均可能导致委托延迟或失败，UPlus 已采取重试与告警机制，但不对由此产生的损失承担赔偿责任。',
          '请根据自身风险承受能力决定投入金额，切勿使用借贷资金或生活必需资金参与交易。'
        ]
      },
      {
        no: '05',
        title: '服务变更与终止',
        paragraphs: [
          '平台可能因功能升级、策略下架或合规要求调整服务内容，重大变更将提前 7 日通过站内通知与邮件告知。',
          '若您存在恶意套利、接口滥用、违反当地法律法规等行为，平台有权暂停或终止向您提供服务。'
        ]
      },
      {
        no: '06',
        title: '责任限制',
        paragraphs: [
          '在法律允许的最大范围内，UPlus 对因使用本服务产生的间接损失、利润损失或数据损失不承担责任。',
          '您理解并同意：策略参数的选择、投入金额的决定以及由此产生的全部交易结果均由您自主判断、自行承担。UPlus 不对市场行情涨跌、策略盈亏及仓位风险作出任何担保或补偿承诺。'
        ]
      }
    ]
  },
  privacy: {
    intro:
      '我们仅收集为您提供交易服务所必需的信息。您的交易所 API 密钥经加密存储，UPlus 不具备提币权限，也不会将您的个人信息出售给任何第三方。',
    sections: [
      {
        no: '01',
        title: '我们收集的信息',
        paragraphs: [
          '账户信息：邮箱地址、昵称、头像、登录时间与设备指纹，用于账户识别与异常登录检测。',
          '交易授权信息：您绑定的交易所名称与 API 密钥。密钥以 AES-256 加密后存储于独立密钥库，明文不落盘、不写入日志。',
          '交易运行数据：策略配置、委托与成交记录、收益结算明细，用于向您展示账单与收益分析。',
          '我们不会收集您的身份证件、银行卡号、交易所登录密码或钱包助记词。'
        ]
      },
      {
        no: '02',
        title: '信息的使用',
        paragraphs: [
          '您的信息仅用于：执行您授权的交易策略、生成收益与账单报表、进行风险监控与告警、提供客户支持，以及在您同意的前提下发送产品通知。',
          '收益榜单中的用户名默认脱敏展示（如 U***368）。'
        ]
      },
      {
        no: '03',
        title: '信息的共享',
        paragraphs: [
          'UPlus 不出售、不出租您的个人信息。仅在以下情形共享必要信息：向您绑定的交易所传递交易指令；使用云服务商提供基础设施（其无法解密您的密钥）；应司法机关依法定程序要求提供。'
        ]
      },
      {
        no: '04',
        title: '数据安全',
        paragraphs: [
          '全站强制 HTTPS 传输，密钥库与业务库物理隔离，运维访问需双因素认证并留存审计日志。',
          '若发生数据安全事件，我们将在确认后 72 小时内通过邮件与站内通知向受影响用户披露。'
        ]
      },
      {
        no: '05',
        title: '您的权利',
        paragraphs: [
          '您可随时查看、更正个人资料，修改登录密码，或解绑交易所 API 密钥。解绑后我们将不再读取该交易所的任何账户数据。',
          '如需导出您的完整交易与账单数据，可在账单页使用导出功能，或通过帮助中心提交申请。'
        ]
      },
      {
        no: '06',
        title: 'Cookie 与本地存储',
        paragraphs: [
          '我们使用必要的本地存储保存登录状态与界面偏好（如语言、图表周期）。平台不使用第三方广告追踪 Cookie。'
        ]
      }
    ]
  }
}

export default {
  name: 'LegalTermsModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    initialTab: {
      type: String,
      default: 'terms'
    }
  },
  data() {
    return {
      activeTab: this.initialTab === 'privacy' ? 'privacy' : 'terms',
      previousBodyOverflow: ''
    }
  },
  computed: {
    currentContent() {
      return LEGAL_CONTENT[this.activeTab]
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.activeTab = this.initialTab === 'privacy' ? 'privacy' : 'terms'
        if (!this.isMobileViewport()) {
          this.previousBodyOverflow = document.body.style.overflow
          document.body.style.overflow = 'hidden'
        }
        window.addEventListener('keydown', this.onKeydown)
        this.$nextTick(() => {
          if (this.$refs.dialog && !this.isMobileViewport()) {
            this.$refs.dialog.focus({ preventScroll: true })
          }
        })
      } else {
        this.restorePage()
      }
    },
    initialTab(value) {
      if (this.visible) this.selectTab(value)
    }
  },
  beforeDestroy() {
    this.restorePage()
  },
  methods: {
    isMobileViewport() {
      return typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches
    },
    selectTab(tab) {
      this.activeTab = tab === 'privacy' ? 'privacy' : 'terms'
      this.$nextTick(() => {
        if (this.$refs.content) this.$refs.content.scrollTop = 0
      })
    },
    close() {
      this.$emit('close')
    },
    onKeydown(event) {
      if (event.key === 'Escape') this.close()
    },
    restorePage() {
      window.removeEventListener('keydown', this.onKeydown)
      if (!this.isMobileViewport()) document.body.style.overflow = this.previousBodyOverflow
    }
  }
}
</script>

<style scoped>
.legal-modal {
  position: fixed;
  z-index: 150;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(20, 17, 38, 0.46);
}

.legal-modal__panel {
  --legal-title-color: #1b1f2a;
  --legal-body-color: #5a6072;
  --legal-muted-color: #98a0af;
  --legal-primary-color: #1f6bff;
  display: flex;
  width: 720px;
  max-width: 94vw;
  height: 78vh;
  flex-direction: column;
  overflow: hidden;
  color: var(--legal-title-color);
  border-radius: 16px;
  outline: none;
  background: #fff;
  box-shadow: 0 24px 64px rgba(20, 16, 45, 0.28);
  font-family: Manrope, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei",
    sans-serif;
  font-size: 13px;
  font-weight: 400;
  -webkit-font-smoothing: antialiased;
}

.legal-modal__header {
  flex: 0 0 auto;
  padding: 24px 28px 0;
}

.legal-modal__heading-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.legal-modal__heading-row h2 {
  margin: 0;
  color: var(--legal-title-color);
  font-size: 19px;
  font-weight: 800;
  letter-spacing: 0.2px;
  line-height: 26px;
}

.legal-modal__heading-row p {
  margin: 5px 0 0;
  color: var(--legal-muted-color);
  font-size: 12.5px;
  font-weight: 400;
  line-height: 18px;
}

.legal-modal__close {
  display: inline-flex;
  width: 30px;
  height: 30px;
  margin: -2px -4px 0 0;
  padding: 0;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: #8a90a0;
  border: 0;
  border-radius: 8px;
  outline: none;
  background: transparent;
  font-size: 16px;
  cursor: pointer;
  transition: color 0.16s ease, background-color 0.16s ease;
}

.legal-modal__close:hover,
.legal-modal__close:focus-visible {
  color: #5a6072;
  background: #f2f5fa;
}

.legal-modal__tabs {
  display: inline-flex;
  gap: 4px;
  margin-top: 18px;
  padding: 3px;
  border-radius: 9px;
  background: #f2f5fa;
}

.legal-modal__tabs button {
  padding: 7px 18px;
  color: #79808f;
  border: 0;
  border-radius: 7px;
  outline: none;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 19px;
  cursor: pointer;
  transition: color 0.16s ease, background-color 0.16s ease;
}

.legal-modal__tabs button.active {
  color: var(--legal-primary-color);
  background: #fff;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
}

.legal-modal__tabs button:focus-visible {
  box-shadow: 0 0 0 2px rgba(31, 107, 255, 0.16);
}

.legal-modal__content {
  min-height: 0;
  margin-top: 14px;
  padding: 20px 28px 26px;
  flex: 1 1 auto;
  overflow-y: auto;
  border-top: 1px solid #f0f2f6;
  outline: none;
  scrollbar-color: #c7cfdd transparent;
  scrollbar-width: thin;
}

.legal-modal__content::-webkit-scrollbar {
  width: 6px;
}

.legal-modal__content::-webkit-scrollbar-track {
  background: transparent;
}

.legal-modal__content::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: #c7cfdd;
}

.legal-modal__intro {
  margin-bottom: 22px;
  padding: 13px 15px;
  color: #31527f;
  border: 1px solid #e4edfe;
  border-radius: 10px;
  background: #f7faff;
  font-size: 12.5px;
  font-weight: 400;
  line-height: 1.75;
}

.legal-modal__section {
  margin-bottom: 22px;
}

.legal-modal__section h3 {
  display: flex;
  gap: 10px;
  align-items: baseline;
  margin: 0 0 8px;
  color: var(--legal-title-color);
  font-size: 14.5px;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 22px;
}

.legal-modal__section h3 span {
  flex: 0 0 auto;
  color: var(--legal-primary-color);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
}

.legal-modal__section p {
  margin: 0 0 5px;
  padding-left: 26px;
  color: var(--legal-body-color);
  font-size: 13px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 1.9;
  text-wrap: pretty;
}

.legal-modal__contact {
  margin: 0;
  padding-top: 16px;
  color: var(--legal-muted-color);
  border-top: 1px solid #f0f2f6;
  font-size: 12.5px;
  line-height: 1.8;
}

.legal-modal__footer {
  display: flex;
  padding: 14px 28px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
  border-top: 1px solid #f0f2f6;
  background: #fbfcfe;
}

.legal-modal__footer button {
  padding: 9px 22px;
  flex: 0 0 auto;
  color: #fff;
  border: 0;
  border-radius: 8px;
  outline: none;
  background: var(--legal-primary-color);
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 700;
  line-height: 20px;
  cursor: pointer;
  transition: background-color 0.16s ease, box-shadow 0.16s ease;
}

.legal-modal__footer button:hover {
  background: #1857d6;
}

.legal-modal__footer button:focus-visible {
  box-shadow: 0 0 0 3px rgba(31, 107, 255, 0.2);
}

.legal-modal-fade-enter-active,
.legal-modal-fade-leave-active {
  transition: opacity 0.18s ease;
}

.legal-modal-fade-enter-active .legal-modal__panel,
.legal-modal-fade-leave-active .legal-modal__panel {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.legal-modal-fade-enter,
.legal-modal-fade-leave-to {
  opacity: 0;
}

.legal-modal-fade-enter .legal-modal__panel,
.legal-modal-fade-leave-to .legal-modal__panel {
  opacity: 0;
  transform: translateY(6px) scale(0.98);
}

@media (max-width: 640px) {
  .legal-modal {
    padding: 10px;
  }

  .legal-modal__panel {
    width: 100%;
    max-width: none;
    height: 92vh;
    border-radius: 14px;
  }

  .legal-modal__header {
    padding: 20px 18px 0;
  }

  .legal-modal__content {
    padding: 18px 18px 24px;
  }

  .legal-modal__footer {
    padding: 12px 18px;
  }
}
</style>
